import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  Input, Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { TemplateSection, TemplateStack } from '../template-shell';
import type { ProductTemplateProps } from '../template-props';
import type { ContractFormValues } from '../../contract-form';

/**
 * SSL certificate billing periods. Legacy `PRIVATE_DOMAIN_BILLING_CYCLE` — one
 * entry per year term; the value is the number of years the cert is registered.
 */
const BILLING_CYCLE_OPTIONS = [
  { value: '1', label: '1 year' },
  { value: '2', label: '2 years' },
  { value: '3', label: '3 years' },
  { value: '4', label: '4 years' },
  { value: '5', label: '5 years' },
];

/**
 * SSL.SSL template — the certificate is configured by the domain it secures plus
 * a billing period. Both are written into `products.{index}.service_domain_metadata`
 * (`domain_value`, `billing_cycle`); the base product line already owns the
 * register price + prepaid + service dates, so those are not duplicated here.
 * `@workspace`'s ProductLine has no `term` field, so the billing cycle lives in
 * metadata rather than as a product-line column.
 */
export function SslTemplate({ index }: ProductTemplateProps) {
  const { control, setValue, getValues } = useFormContext<ContractFormValues>();
  const metaPath = `products.${index}.service_domain_metadata` as const;
  const meta = useWatch({ control, name: metaPath }) as Record<string, unknown> | undefined;

  // Seed a default billing cycle once so the payload always carries one.
  useEffect(() => {
    const current = getValues(metaPath) as Record<string, unknown> | undefined;
    if (current && current.billing_cycle != null) return;
    setValue(metaPath, { ...(current ?? {}), billing_cycle: '1' } as never, { shouldDirty: true });
  }, [metaPath, getValues, setValue]);

  const set = (key: string, value: unknown) =>
    setValue(metaPath, { ...(meta ?? {}), [key]: value } as never, { shouldDirty: true });

  return (
    <TemplateStack>
      <TemplateSection title="SSL certificate">
        <Field label="SSL for domain name">
          {(p) => (
            <Input
              {...p}
              placeholder="example.com"
              value={(meta?.domain_value as string) ?? ''}
              onChange={(e) => set('domain_value', e.target.value)}
            />
          )}
        </Field>

        <Controller
          control={control}
          name={`${metaPath}.billing_cycle` as never}
          render={() => (
            <Field label="Registration period">
              {(p) => (
                <Select
                  value={(meta?.billing_cycle as string) || '1'}
                  onValueChange={(v) => set('billing_cycle', v)}
                >
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BILLING_CYCLE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          )}
        />
      </TemplateSection>
    </TemplateStack>
  );
}
